import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HolidayHeroDecor, HolidayParticles } from "./HolidayDecor";
import HolidayFrame from "./HolidayFrame";

const EASE = [0.22, 1, 0.36, 1];
const LINES = ["Where every pup", "leaves looking", "pawtastic."];

function HeroSlideshow({ slides, controls = true, holiday = null }) {
  const accent = holiday?.accent;
  const reducedMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [pausedUntil, setPausedUntil] = useState(0);
  const touchX = useRef(null);
  const count = slides.length;

  useEffect(() => {
    if (count < 2 || reducedMotion) return undefined;
    const t = setInterval(() => {
      if (Date.now() < pausedUntil) return;
      setIndex((i) => (i + 1) % count);
    }, 5200);
    return () => clearInterval(t);
  }, [count, reducedMotion, pausedUntil]);

  const go = (dir) => {
    if (count < 2) return;
    setIndex((i) => (i + dir + count) % count);
    setPausedUntil(Date.now() + 12000);
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  if (!count) return null;
  const current = slides[index % count];
  const showControls = controls && count > 1;

  return (
    <motion.figure
      initial={{ opacity: 0, scale: 0.94, y: 40 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
      className="relative mx-auto w-full max-w-sm"
      data-testid="hero-slideshow"
    >
      <p data-testid="hero-slideshow-kicker" className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-lavender-600" style={accent ? { color: accent } : undefined}>
        Fresh from the Grass Wall
      </p>
      <div className="relative">
      <div
        className="group relative aspect-square overflow-hidden rounded-lg bg-lavender-100 shadow-lift"
        style={accent ? { boxShadow: `0 0 0 4px ${accent}55, 0 24px 48px -12px rgba(28,25,23,.3)` } : undefined}
        onTouchStart={showControls ? onTouchStart : undefined}
        onTouchEnd={showControls ? onTouchEnd : undefined}
        onKeyDown={showControls ? (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); } : undefined}
        tabIndex={showControls ? 0 : undefined}
        role={showControls ? "region" : undefined}
        aria-label={showControls ? "Grass wall photos. Use the arrows or swipe to see more." : undefined}
        data-testid="hero-slideshow-frame"
      >
        {slides.map((s, i) => (
          <motion.img
            key={s.id || s.imageUrl}
            src={s.imageUrl}
            alt={s.caption || "Freshly groomed dog at the grass wall"}
            data-testid={`hero-slide-${i}`}
            initial={false}
            animate={{ opacity: i === index ? 1 : 0 }}
            transition={{ duration: reducedMotion ? 0 : 1.1, ease: "easeInOut" }}
            className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover object-center"
            draggable={false}
          />
        ))}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-stone-950/55 via-stone-950/5 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4">
          {current.caption ? (
            <figcaption
              key={current.id || current.imageUrl || index}
              data-testid="hero-slide-caption"
              className={`min-w-0 rounded-md border border-white/25 bg-lavender-900/55 px-3.5 py-2 shadow-soft backdrop-blur-md ${reducedMotion ? "" : "caption-in"}`}
            >
              <span className={`block font-serif leading-snug text-white [overflow-wrap:anywhere] ${current.caption.length > 40 ? "line-clamp-3 text-sm sm:text-base" : "line-clamp-2 text-base sm:text-lg"}`}>{current.caption}</span>
            </figcaption>
          ) : <span />}
          {showControls && (
            <div className="flex shrink-0 gap-1.5" data-testid="hero-slideshow-controls">
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Previous photo"
                data-testid="hero-slide-prev"
                className="grid h-8 w-8 place-items-center rounded-md border border-white/25 bg-lavender-900/45 text-white backdrop-blur-md transition-[background-color,transform] duration-200 hover:bg-lavender-700/80 hover:-translate-y-0.5 active:translate-y-0"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Next photo"
                data-testid="hero-slide-next"
                className="grid h-8 w-8 place-items-center rounded-md border border-white/25 bg-lavender-900/45 text-white backdrop-blur-md transition-[background-color,transform] duration-200 hover:bg-lavender-700/80 hover:-translate-y-0.5 active:translate-y-0"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
      <HolidayFrame theme={holiday} />
      </div>
    </motion.figure>
  );
}

export default function Hero({ settings, slides = [], holiday = null }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const tel = `tel:+1${(settings.phone || "").replace(/\D/g, "")}`;
  const accent = holiday ? { color: holiday.accent } : undefined;

  return (
    <section ref={ref} data-testid="hero-section" data-holiday={holiday?.id || "off"} className="relative overflow-hidden bg-lavender-50">
      {holiday && <HolidayHeroDecor theme={holiday} />}
      {holiday && <HolidayParticles theme={holiday} />}
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:py-20">
        <motion.div style={{ opacity: fade }} className="flex flex-col justify-center lg:col-span-7">
          <h1 className="font-serif text-5xl leading-[1.05] tracking-tight text-stone-900 sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-lavender-700" : ""}`}
                  style={i === 2 ? accent : undefined}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.15 + i * 0.14, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg"
            data-testid="hero-subcopy"
          >
            Baths, haircuts and nail care for your dog,
            finished with a fresh photo at our grass wall.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a href={tel} data-testid="hero-call-button" className="btn-primary inline-flex items-center justify-center px-7 py-3.5 text-sm font-bold">
              <span>Call {settings.phone}</span>
            </a>
            <Link to="/services" data-testid="hero-services-button" className="btn-secondary inline-flex items-center justify-center px-7 py-3.5 text-sm font-bold">
              Explore services
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }} className="mt-9 space-y-2 text-sm text-stone-500">
            <p data-testid="hero-hours">{settings.hours}</p>
            <p data-testid="hero-address">{settings.address}</p>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <HeroSlideshow slides={slides.filter((s) => s.imageUrl)} controls={settings.slideshowControls !== false} holiday={holiday} />
        </div>
      </div>
    </section>
  );
}
